import { X } from '@phosphor-icons/react';
import { Form, TableCell, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { RightSidebar } from '../../../components/Sidebar';
import { AppTable, ItemImage } from '../../../components';
import {
  BoldText,
  ColoredPill,
  DropdownFormField,
  MaskedNumberFormField,
  MuteText,
  StyledButton,
  StyledCheckbox,
} from '../../../elements';
import { CommonUtility, ShippingSpeedOptions, ToastMessage } from '../../../utility';
import { PackagingService } from '../../../utility/services/packaging';

const minMaxValidation = (number, max = 999) => !(number > max || number < 0);

const SizeFormGroup = styled(Form.Group)`
  justify-content: space-between;
  margin-bottom: 0 !important;
  input {
    flex: 1;
  }

  .icon {
    min-width: 18px;
    margin-top: 35px;
    color: ${({ theme }) => theme.colors.grey};
  }
`;

const RateContainer = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.box};
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 5px;
  height: 100%;
  width: 100%;
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.colors.white};

  &:hover {
    border: 2px solid ${({ theme }) => theme.colors.grey};
  }

  &.selected {
    border: 2px solid ${({ theme }) => theme.colors.primary};
    box-sizing: border-box;
  }
`;

const InputLabel = styled.span`
  margin-left: 0 !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  display: flex !important;
  align-items: center !important;
  height: 38px !important;
  border-left: none !important;
  position: absolute;
  right: 8px;
  color: ${({ theme }) => theme.colors.grey};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ItemTableContainer = styled.div`
  max-height: 50vh;
  overflow-y: auto;
`;

const shippingSchema = yup
  .object({
    pounds: yup
      .number()
      .transform((value, originalValue) =>
        isNaN(originalValue) || originalValue === '' ? 0 : value,
      )
      .min(0)
      .default(0),
    ounces: yup
      .number()
      .transform((value, originalValue) =>
        isNaN(originalValue) || originalValue === '' ? 0 : value,
      )
      .min(0)
      .default(0),
    length: yup.number().positive().required('Length is required'),
    width: yup.number().positive().required('Width is required'),
    height: yup.number().positive().required('Height is required'),
    orderSpeed: yup.string().required('Order Speed is required'),
  })
  .test('weightRequired', null, function (item) {
    if ((item?.pounds || 0) <= 0 && (item?.ounces || 0) <= 0) {
      return this.createError({
        path: 'pounds',
        message: 'Weight should have value',
      });
    }
    return true;
  });

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  .footer-action {
    position: sticky;
    bottom: 0px;
  }
`;

const decimalMask = {
  integerLimit: 3,
  allowDecimal: true,
  decimalSymbol: '.',
  decimalLimit: 2,
};

export function CreateLabel({
  showCreateLabel,
  closeCreateLabel,
  data,
  pickList,
  providerId,
  refresh,
  isLabelCorrection = false,
  labelToCorrect,
}) {
  const [rates, setRates] = useState();
  const [selectedRate, setSelectedRate] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [itemChecked, setItemChecked] = useState({});

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(shippingSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  const [ounces, orderSpeed, height, length, pounds, width] = watch([
    'ounces',
    'orderSpeed',
    'height',
    'length',
    'pounds',
    'width',
  ]);

  // TODO: zeroing the ounces drops the remainder (20 oz becomes 1 lb 0 oz). Should carry oz % 16.
  // Same copy exists in six label/rate forms; extract one helper.
  useEffect(() => {
    if (ounces > 15) {
      setValue('ounces', 0);
      setValue('pounds', (getValues('pounds') || 0) + 1);
    }
  }, [ounces]);

  useEffect(() => {
    if (!showCreateLabel) {
      reset({
        orderSpeed: 'Standard',
      });
      setSelectedRate(-1);
      setRates([]);
      setItemChecked({});
    }
  }, [showCreateLabel]);

  useEffect(() => {
    if (!showCreateLabel || !isLabelCorrection || !Array.isArray(pickList)) {
      return;
    }

    setItemChecked(
      pickList.reduce((selected, item) => {
        if (item?.allocationId && item?.inventory?.product?.id) {
          selected[item.allocationId] = item.inventory.product.id;
        }
        return selected;
      }, {}),
    );
  }, [isLabelCorrection, pickList, showCreateLabel]);

  useEffect(() => {
    setSelectedRate(-1);
    setRates([]);
  }, [itemChecked, orderSpeed, height, length, pounds, width, ounces]);

  const columns = [
    { text: '', key: 'action', width: 1, textAlign: 'left' },
    { text: 'Product', key: 'item', width: 8, textAlign: 'left' },
    { text: 'Location', key: 'location', width: 3, textAlign: 'left' },
  ];

  const getSelectedLabelItems = () => {
    const selectedAllocationIds = Object.entries(itemChecked)
      .filter(([, itemId]) => !!itemId)
      .map(([allocationId]) => allocationId);

    const itemObject = {};
    selectedAllocationIds.forEach(allocationId => {
      const itemId = itemChecked[allocationId];
      if (itemObject[itemId]) {
        itemObject[itemId] += 1;
      } else {
        itemObject[itemId] = 1;
      }
    });

    return {
      selectedAllocationIds,
      items: Object.keys(itemObject).map(itemId => ({
        itemId,
        qty: itemObject[itemId],
      })),
    };
  };

  const getRates = async formValues => {
    const { items } = getSelectedLabelItems();

    const payload = {
      orderId: data?._id,
      fulfillmentProviderId: providerId,
      orderSpeed: formValues.orderSpeed,
      packages: [
        {
          pounds: Number(formValues.pounds || 0),
          ounces: Number(formValues.ounces || 0),
          width: Number(formValues.width || 0),
          height: Number(formValues.height || 0),
          length: Number(formValues.length || 0),
          items,
        },
      ],
    };

    try {
      setLoading(true);
      const res = await PackagingService.shippingRates(payload);
      setRates(res);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const generateLabel = async formValues => {
    if (selectedRate === -1) {
      ToastMessage.error('No Rate Selected');
      return;
    }

    if (isLabelCorrection && !labelToCorrect?._id) {
      ToastMessage.error('No label selected to correct');
      return;
    }

    const { selectedAllocationIds, items } = getSelectedLabelItems();

    const payload = {
      orderId: data?._id,
      fulfillmentProviderId: providerId,
      orderSpeed: formValues.orderSpeed,
      force: 'true',
      mode: CommonUtility.isStagingEnv() ? 'test' : 'production',
      allocationIds: selectedAllocationIds,
      ...(isLabelCorrection && { labelId: labelToCorrect?._id }),
      packages: [
        {
          pounds: Number(formValues.pounds || 0),
          ounces: Number(formValues.ounces || 0),
          width: Number(formValues.width || 0),
          height: Number(formValues.height || 0),
          length: Number(formValues.length || 0),
          provider: filteredRates[selectedRate]?.provider.value,
          service: filteredRates[selectedRate]?.service.value,
          package: filteredRates[selectedRate]?.package.value,
          items,
        },
      ],
    };
    try {
      setLoading(true);
      if (isLabelCorrection) {
        await PackagingService.lableCorrection(payload);
        ToastMessage.success('Label corrected successfully');
      } else {
        await PackagingService.createLabel(payload);
        ToastMessage.success('Label generated successfully');
      }
      refresh();
      closeCreateLabel();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const packageRate = useMemo(() => {
    if (CommonUtility.isValidObject(rates?.packages?.[0])) {
      return rates?.packages?.[0];
    }
    return null;
  }, [rates]);

  useEffect(() => {
    if (CommonUtility.isValidArray(packageRate?.rates)) {
      let min = Infinity;
      let indexToSelect = -1;
      packageRate.rates.forEach((rate, i) => {
        if (!rate.error && rate.rate < min) {
          min = rate.rate;
          indexToSelect = i;
        }
      });
      setSelectedRate(indexToSelect);
    }
  }, [packageRate]);

  const filteredRates = useMemo(
    () => packageRate?.rates?.filter(x => !x.error),
    [packageRate],
  );

  const toggleCheck = (allocationId, value) => {
    setItemChecked(itemChecked => ({
      ...itemChecked,
      [allocationId]: value,
    }));
  };

  const hasItemSelected = useMemo(
    () => !!Object.values(itemChecked).some(selected => !!selected),
    [itemChecked],
  );

  return (
    <RightSidebar open={showCreateLabel} onClose={closeCreateLabel} closeOnOutsideClick>
      <div className="header d-flex justify-content-between">
        <h3>{isLabelCorrection ? 'Correct Label' : 'Create Label'}</h3>
        <X size={20} className="cursor-pointer" onClick={closeCreateLabel} />
      </div>
      <Container>
        <span>
          {isLabelCorrection
            ? 'Select Items to include in the replacement label'
            : 'Select Items to include in Label'}
        </span>
        <ItemTableContainer>
          <AppTable
            data={pickList}
            columns={columns}
            RowComponent={<ItemRow itemChecked={itemChecked} toggleCheck={toggleCheck} />}
            noShadow
            wrapperClass="m-0"
          />
        </ItemTableContainer>
        <StyledForm
          className="mt-2"
          onSubmit={
            selectedRate === -1 ? handleSubmit(getRates) : handleSubmit(generateLabel)
          }
        >
          <div>
            <span>Box Dimensions</span>
            <Form.Group widths="equal" className="mb-0 mt-2">
              <MaskedNumberFormField
                label="Weight"
                name="pounds"
                error={errors.pounds}
                hint={errors.pounds?.message}
                defaultValue=""
                control={control}
                width={6}
                min={0}
                placeholder="lbs"
                inputRight={<InputLabel>lbs</InputLabel>}
                required
                maskOptions={decimalMask}
              />
              <MaskedNumberFormField
                label={<br />}
                name="ounces"
                error={errors.pounds}
                control={control}
                width={6}
                min={0}
                placeholder="oz"
                defaultValue={0}
                inputRight={<InputLabel>oz</InputLabel>}
                minMaxValidation={num => minMaxValidation(num, 99)}
                maskOptions={{
                  ...decimalMask,
                  integerLimit: 2,
                }}
              />
            </Form.Group>
            <SizeFormGroup>
              <MaskedNumberFormField
                label="Size (in.)"
                name="length"
                error={errors.length}
                hint={errors.length?.message}
                control={control}
                min={0}
                placeholder="L"
                defaultValue={0}
                required
                maskOptions={decimalMask}
              />
              <X className="icon" size={20} />
              <MaskedNumberFormField
                label={<br />}
                name="width"
                hint={errors.width?.message}
                error={errors.width}
                control={control}
                min={0}
                placeholder="W"
                defaultValue={0}
                maskOptions={decimalMask}
              />
              <X className="icon" size={20} />
              <MaskedNumberFormField
                label={<br />}
                hint={errors.height?.message}
                name="height"
                error={errors.height}
                control={control}
                min={0}
                placeholder="H"
                defaultValue={0}
                maskOptions={decimalMask}
              />
            </SizeFormGroup>
            <DropdownFormField
              name="orderSpeed"
              label="Speed"
              control={control}
              error={errors?.orderSpeed}
              hint={errors?.orderSpeed?.message}
              options={ShippingSpeedOptions}
            />
            <div className="row mr-2">
              {filteredRates?.map((rate, i) => (
                <div className="col-6 mt-2 pl-0 pr-0" onClick={() => setSelectedRate(i)}>
                  <RateContainer
                    className={`${selectedRate === i ? 'selected' : ''} ${
                      i % 2 === 0 ? '' : 'ml-2'
                    }`}
                  >
                    <div className="d-flex flex-column">
                      <BoldText>{rate?.provider?.key}</BoldText>
                      <MuteText>{rate.service?.key}</MuteText>
                    </div>
                    <span>{CommonUtility.currencyFormat(rate.rate)}</span>
                  </RateContainer>
                </div>
              ))}
            </div>
          </div>
          <div className="footer-action">
            {selectedRate === -1 ? (
              <StyledButton
                disabled={!hasItemSelected}
                loading={loading}
                type="submit"
                className="w-100"
              >
                Get Rates
              </StyledButton>
            ) : (
              <StyledButton loading={loading} type="submit" className="w-100">
                {isLabelCorrection ? 'Correct Label' : 'Generate Label'}
              </StyledButton>
            )}
          </div>
        </StyledForm>
      </Container>
    </RightSidebar>
  );
}

function ItemRow({ item, toggleCheck, itemChecked }) {
  return (
    <TableRow>
      <TableCell>
        <StyledCheckbox
          checked={!!itemChecked[item.allocationId]}
          onChange={(e, { checked }) =>
            toggleCheck(item.allocationId, checked ? item.inventory?.product?.id : null)
          }
        />
      </TableCell>
      <TableCell className="py-0">
        <div className="d-flex">
          <ItemImage
            item={item?.inventory?.product}
            imageKey="images"
            displayImageKey="display_image"
            size="mini"
          />
          <div className="ml-2">
            <span>{item?.inventory?.product?.name}</span>
            <div className="d-flex">
              <MuteText>SKU</MuteText>
              <span className="ml-2">{item?.inventory?.product?.sku}</span>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <ColoredPill color={item?.inventory?.zone?.color} mini>
          {item?.inventory?.location?.code}
        </ColoredPill>
      </TableCell>
    </TableRow>
  );
}
